import React from 'react';
import { AlertTriangle, CheckCircle2, HelpCircle, XCircle } from 'lucide-react';
import type { RuleStatus, UspsRule } from './uspsVetting.types';

export const STATUS_LABEL: Record<RuleStatus, string> = {
  PASS: 'Pass',
  FAIL: 'Fail',
  REVIEW: 'Check',
  NOT_EVALUATED: 'Not evaluated',
};

const STATUS_STYLE: Record<RuleStatus, { chip: string; icon: React.ReactNode }> = {
  PASS: { chip: 'bg-green-100 text-green-700', icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> },
  FAIL: { chip: 'bg-red-100 text-red-700', icon: <XCircle className="h-4 w-4" aria-hidden="true" /> },
  REVIEW: { chip: 'bg-yellow-100 text-yellow-700', icon: <AlertTriangle className="h-4 w-4" aria-hidden="true" /> },
  NOT_EVALUATED: { chip: 'bg-gray-100 text-gray-600', icon: <HelpCircle className="h-4 w-4" aria-hidden="true" /> },
};

const BASIS_LABEL: Record<UspsRule['basis'], string> = {
  data: '',
  inferred: 'Inferred',
  external: 'Needs outside data',
};

export const UspsRuleRow: React.FC<{ rule: UspsRule }> = ({ rule }) => {
  const style = STATUS_STYLE[rule.status];
  return (
    <li className="border-t border-gray-200 py-3 first:border-t-0">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-x-3">
        <span className={`inline-flex shrink-0 items-center gap-1 self-start rounded-full px-2.5 py-0.5 text-xs font-semibold sm:w-32 sm:justify-center ${style.chip}`}>
          {style.icon}
          {STATUS_LABEL[rule.status]}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900">
            {rule.text}
            {rule.autoFail && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-red-700">Automatic fail</span>}
            {BASIS_LABEL[rule.basis] && <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">{BASIS_LABEL[rule.basis]}</span>}
          </p>
          {(rule.value || rule.threshold) && (
            <p className="mt-0.5 text-sm text-gray-700">
              {rule.value && <span className="font-semibold">{rule.value}</span>}
              {rule.threshold && <span className="text-gray-500">{rule.value ? ' · ' : ''}limit {rule.threshold}</span>}
            </p>
          )}
          <p className="mt-0.5 text-sm text-gray-600">{rule.detail}</p>
          {rule.partial && <p className="mt-0.5 text-xs italic text-gray-500">Partial check: {rule.partial}</p>}
          {rule.evidence && rule.evidence.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-xs text-gray-600">
              {rule.evidence.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
          {rule.status !== 'NOT_EVALUATED' && <p className="mt-0.5 text-[11px] text-gray-400">Source: {rule.source}{rule.asOf ? ` · as of ${rule.asOf}` : ''}</p>}
        </div>
      </div>
    </li>
  );
};
